export interface IInvoiceList {
  totalCount: number
  totalPageCount: number
  resultCount: number
  morePages: boolean
  currentPage: number
  result: IInvoice[]
}

export interface IInvoice {
  id: string
  client: {
    id: string
    name: string
    hasDepartments: boolean
  }
  invoice: IInvoiceInformation
  approval: IInvoiceApproval
  approvals?: IInvoiceArchiveApproval[]
  isPreviouslyProcessed?: boolean
  approvalId?: string
  payments?: IInvoicePayment[]
  previousInvoices?: IInvoiceList
  lines?: IInvoiceLine[]
}

export interface IInvoiceInformation {
  number: string
  date: Date
  paymentDeadline: Date
  accountNumber: number
  amount: number
  description: string
  attachmentIds: string[]
  supplier: ISupplier
  isPaid?: boolean
}

export interface ISupplier {
  id: string
  name: string
  organizationNumber: string
  phoneNumber: string
  email: string
  addressLine1: string
  addressLine2: string
  addressLine3: string
  zipCode: string
  city: string
  countryCode: string
}

export interface IInvoiceApproval {
  id: string
  deadline: Date
  onHoldUntil: Date
  onHoldByNameId: string
  onHoldByName: string
  approvals: IInvoiceApprover[]
  events: IInvoiceEvents[]
  rights: IInvoiceUserRights
  statusText: string
  statusId: InvoiceStatusEnum
  isCompleted: boolean
  isOnHold: boolean
  isRejected: boolean
  isPreviouslyProcessed: boolean
  processVersion: 'V1_Classic' | 'V2_NXT'
}

export interface IInvoiceApprover {
  approvers: {
    id: string
    navneregisterId: string
    utvalgMedlemsFunksjonsnr: number
    godkjennerNavn: string
    godkjennerType: 'Styrefunksjon' | 'Person'
  }[]
  approvalTypeId: string
  processedByName: string
  processedByNameId: string
  processed: Date
  id: string
  name: string
  nameId: string
  priority: number
  role: string
  roleId: string
  status: string
  statusId: InvoiceStatusEnum
  syncStatus: string
}

export interface IInvoiceEvents {
  date: Date
  id: string
  name: string
  nameId: string
  text: string
  state: InvoiceActionTypeEnum
}

export interface IInvoiceUserRights {
  approve: boolean
  reject: boolean
  onHold: boolean
  attest: boolean
  undoApprove: boolean
  undoReject: boolean
  undoOnHold: boolean
  undoAttest: boolean
  transferApproval: boolean
  missingRequiredAllocationRules: boolean
  lineIdsForTransfer: string[]
}

export interface IInvoiceTransferApprovers {
  name: string
  nameId: string
  roleModel: string[]
}

export interface IInvoiceFilters {
  searchText: string
  scope: string
  status?: IInvoiceFilterStatus
  sort: string
  sortorder: string
  clientId: string | null
  take: number
  skip: number
  invoiceAmountFrom?: number
  invoiceAmountTo?: number
  invoiceDateFrom?: Date
  invoiceDateTo?: Date
  deadlineDateFrom?: Date
  deadlineDateTo?: Date
}

export interface IInvoiceFilterStatus {
  forProcessing: boolean
  rejectedOrOnHold: boolean
  completed: boolean
}

export interface IInvoiceLine {
  id: string
  text: string
  amount: number
  dimension: IDimension
}

export interface IDimension {
  postedClient: string
  account: string
  department: string
  service: string
  purpose: string
  project: string
  facility: string
  operatingExpenses: string
  maintenance: string
  mva: string
  numberOfAccrualMonths: string
  freePeriods: string
  expandedAccounting: string
  specifiedAccounting: string
  allocationRule: {
    id: number
    name: string
  }
}

export interface IInvoiceArchiveApproval {
  events: IInvoiceEvents[]
  id: string
  isHistoric: boolean
  registered: Date
  text: string
}

export interface IInvoicePayment {
  date: Date
  text: string
  amount: number
}

export interface IAccountantInstruction {
  id: string
  instruks: string
  changedById: string
  changedByName: string
  dateChanged: string
}

export interface IManageAccountantInstruction {
  invoiceId: string
  instruction?: string
  isCreate?: boolean
  requiresFollowUp?: boolean
}

export interface IInvoiceParams extends Record<string, string | undefined> {
  id: string
  approvalId?: string
  attachmentId?: string
}

export interface IInvoiceTransferApproval {
  invoiceId: string
  invoiceLineId: string
  nameId: string
}

export interface IProcessInvoice {
  invoiceId: string
  state: InvoiceActionTypeEnum
  comment: string
  onHoldUntil?: Date
}

export enum InvoiceStatusEnum {
  attest = 'Attest',
  approve = 'Approve',
  ongoing = 'Ongoing',
  rejected = 'Rejected',
  onHold = 'OnHold',
  approved = 'Approved',
  notSet = 'NotSet',
  godkjenning = 'Godkjenning',
  avvist = 'Avvist',
  godkjent = 'Godkjent',
  overfort = 'Overfort',
  attestering = 'Attestering',
  attestert = 'Attestert',
}

export enum InvoiceSortFieldEnum {
  number = 'InvoiceNumber',
  supplier = 'SupplierName',
  client = 'ClientName',
  deadline = 'DeadlineDate',
  amount = 'InvoiceAmount',
  paymentDeadline = 'PaymentDeadlineDate',
  invoiceDate = 'InvoiceDate',
  none = 'None',
}

export enum InvoiceSortOrderEnum {
  asc = 'Ascending',
  desc = 'Descending',
}

export enum InvoiceScopeEnum {
  all = 'All',
  mine = 'Mine',
  board = 'Board',
  notPaid = 'NotPaid',
}

export enum InvoiceActionTypeEnum {
  reject = 'Reject',
  undoReject = 'UndoReject',
  onHold = 'OnHold',
  undoOnHold = 'UndoOnHold',
  attest = 'Attest',
  undoAttest = 'UndoAttest',
  approve = 'Approve',
  undoApprove = 'UndoApprove',
  comment = 'Comment',
  transfer = 'Transfer',
}

export type InvoiceType = 'archive' | 'historic' | 'approval'
